/**
 * The current route, and how to change it.
 *
 * `history.pushState` plus a `popstate` listener, which is the whole of what
 * this app needs from a router. Real URLs rather than hash fragments, because
 * the confirmation links the backend puts in email are real paths and a hash
 * would never survive the round trip through a mail client.
 */

import { useCallback, useEffect, useState } from 'react';
import { parseRoute, pathFor, type Route } from './routes';

function currentPath(): string {
  return typeof window === 'undefined' ? '/' : window.location.pathname;
}

function currentSearch(): string {
  return typeof window === 'undefined' ? '' : window.location.search;
}

export interface Navigation {
  route: Route;
  navigate: (route: Route, options?: { replace?: boolean }) => void;
}

export function useRoute(): Navigation {
  const [route, setRoute] = useState<Route>(() => parseRoute(currentPath(), currentSearch()));

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onPop = () => setRoute(parseRoute(currentPath(), currentSearch()));
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const navigate = useCallback((next: Route, options: { replace?: boolean } = {}) => {
    const path = pathFor(next);
    if (typeof window !== 'undefined' && window.history) {
      /*
       * Tapping the tab you are already on must not push a history entry.
       *
       * It did, and that is a navigation defect a learner reads as the app
       * being broken: tap Write three times while reading it, then press Back
       * three times and nothing appears to happen — you are walking back
       * through three identical entries for the page you are looking at. Rapid
       * taps on one tab made it arbitrarily deep.
       */
      const samePlace = `${window.location.pathname}${window.location.search}` === path;
      if (options.replace || samePlace) window.history.replaceState(null, '', path);
      else window.history.pushState(null, '', path);
    }
    setRoute(next);
    // A route change is a page change for anyone not looking at the screen; the
    // screens themselves move focus to their own heading on mount.
  }, []);

  return { route, navigate };
}
